import { Observable } from 'rxjs/Rx'
import { combineEpics } from 'redux-observable'
import { alertSuccess, alertErrorDefault } from 'store/ducks/alert'
import { create, update, remove, list } from 'store/services/vehicle'
import * as authenticationDuck from 'store/ducks/authentication'
import { intl } from 'i18n'

class DataTransformer {
  static transform(payload) {
    return {
      ...payload,
      options: Object.keys(payload.options || {}).filter((key) => payload.options[key] === true),
    }
  }

  static reverseTransform(payload) {
    return {
      ...payload,
      options: payload.options ? payload.options.reduce((result, option) => ({ ...result, [option]: true }), {}) : {},
    }
  }
}

// Actions
export const CREATE_REQUEST = 'vehicle/CREATE_REQUEST'
export const CREATE_SUCCESS = 'vehicle/CREATE_SUCCESS'
export const CREATE_FAILURE = 'vehicle/CREATE_FAILURE'

export const UPDATE_REQUEST = 'vehicle/UPDATE_REQUEST'
export const UPDATE_SUCCESS = 'vehicle/UPDATE_SUCCESS'
export const UPDATE_FAILURE = 'vehicle/UPDATE_FAILURE'

export const DELETE_REQUEST = 'vehicle/DELETE_REQUEST'
export const DELETE_SUCCESS = 'vehicle/DELETE_SUCCESS'
export const DELETE_FAILURE = 'vehicle/DELETE_FAILURE'

export const LIST_REQUEST = 'vehicle/LIST_REQUEST'
export const LIST_SUCCESS = 'vehicle/LIST_SUCCESS'
export const LIST_FAILURE = 'vehicle/LIST_FAILURE'

export const INIT_FORM_DATA = 'vehicle/INIT_FORM_DATA'
export const RESET_FORM_DATA = 'vehicle/RESET_FORM_DATA'
export const OPEN_MODAL = 'vehicle/OPEN_MODAL'
export const CLOSE_MODAL = 'vehicle/CLOSE_MODAL'

// Reducer
const INITIAL_STATE = {
  edit: {
    loading: false,
    violations: [],
    initialValues: {},
  },
  list: {
    loading: false,
    result: [],
    entities: {},
  },
  modalIsOpen: false,
}

export function reducer(state = INITIAL_STATE, action = {}) {
  switch (action.type) {
    case CREATE_REQUEST:
    case UPDATE_REQUEST:
    case DELETE_REQUEST:
      return {
        ...state,
        edit: {
          ...INITIAL_STATE.edit,
          initialValues: { ...state.edit.initialValues },
          loading: true,
        },
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        edit: {
          ...INITIAL_STATE.edit,
        },
        list: {
          ...state.list,
          entities: {
            ...state.list.entities,
            [action.result.id]: action.result,
          },
        },
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        edit: {
          ...INITIAL_STATE.edit,
        },
        list: {
          ...state.list,
          result: [...state.list.result, action.result.id],
          entities: {
            ...state.list.entities,
            [action.result.id]: action.result,
          },
        },
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        edit: {
          ...INITIAL_STATE.edit,
        },
        list: {
          ...state.list,
          result: state.list.result.filter((value) => parseInt(value) !== parseInt(action.payload)),
          entities: Object.values(state.list.entities)
            .filter((value) => parseInt(value.id) !== parseInt(action.payload))
            .reduce((result, value) => ({ ...result, [value.id]: value }), {}),
        },
      }
    case CREATE_FAILURE:
    case UPDATE_FAILURE:
    case DELETE_FAILURE:
      return {
        ...state,
        edit: {
          ...INITIAL_STATE.edit,
          violations: action.violations || [],
        },
      }
    case RESET_FORM_DATA:
      return {
        ...state,
        edit: {
          ...INITIAL_STATE.edit,
        },
      }
    case OPEN_MODAL:
      return {
        ...state,
        modalIsOpen: true,
      }
    case CLOSE_MODAL:
      return {
        ...state,
        modalIsOpen: false,
      }
    case LIST_REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          loading: true,
        },
      }
    case LIST_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          result: action.result,
          entities: action.entities,
        },
      }
    case LIST_FAILURE:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
        },
      }
    case INIT_FORM_DATA:
      return {
        ...state,
        edit: {
          ...state.edit,
          initialValues: action.payload,
        },
      }

    default:
      return state
  }
}

// Actions Creators
export const createRequest = (payload) => ({
  type: CREATE_REQUEST,
  payload: DataTransformer.transform(payload),
})

export const createSuccess = (result) => ({
  type: CREATE_SUCCESS,
  result,
})

export const createFailure = (violations) => ({
  type: CREATE_FAILURE,
  violations,
})

export const updateRequest = (payload) => ({
  type: UPDATE_REQUEST,
  payload: DataTransformer.transform(payload),
})

export const updateSuccess = (result) => ({
  type: UPDATE_SUCCESS,
  result,
})

export const updateFailure = (violations) => ({
  type: UPDATE_FAILURE,
  violations,
})

export const deleteRequest = (id) => ({
  type: DELETE_REQUEST,
  payload: id,
})

export const deleteSuccess = (id) => ({
  type: DELETE_SUCCESS,
  payload: id,
})

export const deleteFailure = (violations) => ({
  type: DELETE_FAILURE,
  violations,
})

export const listRequest = () => ({
  type: LIST_REQUEST,
})

export const listSuccess = (result) => {
  const entities = result.reduce((obj, vehicle) => Object.assign(obj, { [vehicle.id]: vehicle }), {})

  return {
    type: LIST_SUCCESS,
    result: Object.keys(entities),
    entities,
  }
}

export const listFailure = () => ({
  type: LIST_FAILURE,
})

export const resetFormData = () => ({
  type: RESET_FORM_DATA,
})

export const openModal = () => ({
  type: OPEN_MODAL,
})

export const closeModal = () => ({
  type: CLOSE_MODAL,
})

export const initFormData = (vehicle) => ({
  type: INIT_FORM_DATA,
  payload: DataTransformer.reverseTransform(vehicle),
})

// Epics
export const epic = combineEpics(
  createRequestEpic,
  updateRequestEpic,
  deleteRequestEpic,
  closeModalOnSuccessEpic,
  resetFormDataOnCloseModalEpic,
  listRequestEpic,
  openModalWhenInitFormDataEpic,
  sendAlertOnCreateSuccessEpic,
  sendAlertOnUpdateSuccessEpic,
  sendAlertOnDeleteSuccessEpic,
  sendAlertOnfailureEpic,
)

export function createRequestEpic(action$) {
  return action$.ofType(CREATE_REQUEST).mergeMap((action) =>
    Observable.ajax(create({ payload: action.payload }))
      .map((res) => createSuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )
          return Observable.of(createFailure(errors))
        } catch (e) {
          return Observable.of(createFailure([intl.formatMessage({ id: 'ducks.server' })]))
        }
      }),
  )
}

export function updateRequestEpic(action$) {
  return action$.ofType(UPDATE_REQUEST).mergeMap((action) =>
    Observable.ajax(update({ payload: action.payload, params: { id: action.payload.id } }))
      .map((res) => updateSuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )
          return Observable.of(updateFailure(errors))
        } catch (e) {
          return Observable.of(updateFailure([intl.formatMessage({ id: 'ducks.server' })]))
        }
      }),
  )
}

export function deleteRequestEpic(action$) {
  return action$.ofType(DELETE_REQUEST).mergeMap((action) =>
    Observable.ajax(remove({ params: { id: action.payload } }))
      .map((res) => deleteSuccess(action.payload))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )
          return Observable.of(deleteFailure(errors))
        } catch (e) {
          return Observable.of(deleteFailure([intl.formatMessage({ id: 'ducks.server' })]))
        }
      }),
  )
}

export function closeModalOnSuccessEpic(action$) {
  return action$.ofType(CREATE_SUCCESS, UPDATE_SUCCESS, DELETE_SUCCESS).mapTo(closeModal())
}

export function resetFormDataOnCloseModalEpic(action$) {
  return action$.ofType(CLOSE_MODAL).mapTo(resetFormData())
}

export function listRequestEpic(action$) {
  return action$.ofType(LIST_REQUEST).mergeMap((action) =>
    Observable.ajax(list())
      .map((res) => listSuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(listFailure())
      }),
  )
}

export function openModalWhenInitFormDataEpic(action$) {
  return action$.ofType(INIT_FORM_DATA).mapTo(openModal())
}

export function sendAlertOnCreateSuccessEpic(action$) {
  return action$.ofType(CREATE_SUCCESS).mapTo(alertSuccess(intl.formatMessage({ id: 'ducks.vehicle.create.success' })))
}

export function sendAlertOnUpdateSuccessEpic(action$) {
  return action$.ofType(UPDATE_SUCCESS).mapTo(alertSuccess(intl.formatMessage({ id: 'ducks.vehicle.update.success' })))
}

export function sendAlertOnDeleteSuccessEpic(action$) {
  return action$.ofType(DELETE_SUCCESS).mapTo(alertSuccess(intl.formatMessage({ id: 'ducks.vehicle.delete.success' })))
}

export function sendAlertOnfailureEpic(action$) {
  return action$.ofType(CREATE_FAILURE, DELETE_FAILURE, UPDATE_FAILURE).mapTo(alertErrorDefault())
}

export const getVehicleList = (state) => state.list.result.map((id) => state.list.entities[id])
